/**
 * Runtime accent substitution for character illustration artwork.
 *
 * Recolouring is a transform on the SVG XML string the native renderer already
 * holds after reading the bundled asset — no re-export, no build step, no extra
 * dependency. Only the tint state does any of this work; the default and mono
 * states stay pure file lookups.
 */

import { getCharacterIllustrationAccent } from './accents';
import type { CharacterIllustrationName } from './names';
import { illustrationTintValue, type IllustrationTint } from './tints';

/**
 * Matches one colour value in a `fill` or `stroke` attribute, whole-value.
 *
 * Attribute-scoped rather than a bare string replace, which would also rewrite
 * the value inside an `id`, a gradient reference, or any longer value that
 * merely begins with this hex. Case-insensitive because SVG hex is case-free.
 *
 * Module-private on purpose. A `g`-flagged regex carries `lastIndex`, so a
 * shared instance answers `.test()` alternately true and false — and the accent
 * drift guard, the only thing holding the declared table to the artwork, would
 * silently pass for about half the set the moment someone hoisted the pattern
 * out of its loop. Callers get the match list instead (below), which is the
 * question they actually have and cannot be held stale.
 */
function colorAttributePattern(value: string): RegExp {
  const escaped = value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  // The leading `(^|\s)` group bounds the attribute name, so `data-fill="…"`
  // and `x-stroke="…"` are not rewritten — in SVG an attribute is always
  // preceded by whitespace or starts the string. Expressed as a captured group
  // rather than a lookbehind deliberately: this runs under Hermes, a lookbehind
  // here would be the only one in the package, and a construction failure would
  // surface inside render where nothing catches it.
  return new RegExp(`(^|\\s)(fill|stroke)="${escaped}"`, 'gi');
}

/**
 * Every `fill`/`stroke` occurrence of one colour value in this markup.
 *
 * The substitution and the accent-table drift guard go through this same
 * builder, so an accent the guard proves reachable is an accent the
 * substitution can actually reach — a second, subtly different matcher on
 * either side would let the guard pass while tinting silently no-ops.
 */
export function matchIllustrationColorAttributes(xml: string, value: string): string[] {
  return [...xml.matchAll(colorAttributePattern(value))].map((match) => match[0]);
}

/**
 * The colour artwork's XML with its declared accent replaced by the tint.
 *
 * Strict where the component layer is forgiving, and deliberately so: a
 * renderer decides what to do about a tint it cannot apply (it renders the
 * default and warns), and by the time it calls this the pairing is already
 * known-good. Reaching here with an untintable pairing therefore means a direct
 * caller of the elements layer got it wrong, which is a programming error worth
 * raising rather than papering over.
 */
export function applyIllustrationTint(
  xml: string,
  name: CharacterIllustrationName,
  tint: IllustrationTint,
): string {
  const replacement = illustrationTintValue(tint);
  const accent = getCharacterIllustrationAccent(name);
  if (accent === null) {
    // Also the unregistered-name case, which the accessor normalises to null —
    // a named error rather than a TypeError from a pattern built on undefined.
    throw new Error(
      `Illustration "${name}" has no tintable accent, so it cannot be tinted "${tint}".`,
    );
  }
  return xml.replace(
    colorAttributePattern(accent),
    (_match, boundary: string, attribute: string) => `${boundary}${attribute}="${replacement}"`,
  );
}
